import { PopoverPlacement } from "@idui/react-popover";
import { EmailOutlined as EmailIcon } from "@mui/icons-material";
import { FormControl, FormHelperText, TextField } from "@mui/material";
import clsx from "clsx";
import { useId, useMemo, type FC } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useDynamicFormsTranslate } from "@ryde/redux";
import {
  getRegisterAttributes,
  ValidationHelpers,
  type ICustomField,
} from "@ryde/shared";

import { FramerTooltip } from "../../common";
import { FullWidthTextFieldWrapper } from "../parts-styled";
import { FieldExplanationIcon } from "./_parts";
import { StyledEmailTemplate } from "./_template-styles";

export const EmailTemplate: FC<ICustomField> = ({
  id,
  name,
  formLabel,
  formAttributes,
  formValidation,
  e2eData = "email-template",
  uniqueAttrId,
}) => {
  const { control, getValues } = useFormContext();
  const generatedId = useId();
  const { formLabelTranslated } = useDynamicFormsTranslate({
    name,
    fallbackText: formLabel,
  });
  const { disabled } = getRegisterAttributes(formAttributes);
  const { t } = useTranslation();

  const email = getValues(name);
  const emailMessage = t("general.EMAIL_SEND");

  const emailClickHandler = (): void => {
    if (!email) {
      return;
    }

    window.location.href = `mailto:${email}`;
  };

  const requiredComputed = useMemo(() => {
    return ValidationHelpers.hasRequiredRule(formValidation);
  }, [formValidation]);

  const inputId = uniqueAttrId ?? `${name}-${generatedId}`;

  return (
    <StyledEmailTemplate>
      <div className="emailField">
        <div className="emailFieldInner">
          <FullWidthTextFieldWrapper>
            <Controller
              name={name}
              control={control}
              defaultValue={""}
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormControl
                    required={requiredComputed}
                    data-e2e-template="email-template"
                    data-e2e-field-name={name}
                    error={!!error}
                  >
                    <TextField
                      {...field}
                      data-e2e={e2eData}
                      data-e2e-id={`${name}_${id || ""}`}
                      label={formLabelTranslated}
                      required={requiredComputed}
                      disabled={disabled}
                      error={!!error}
                      fullWidth
                      dir={"ltr"}
                      type="email"
                      id={inputId}
                    />
                    {/*TODO: add translation solution*/}
                    {error && <FormHelperText>{error.message}</FormHelperText>}
                  </FormControl>
                );
              }}
            />
          </FullWidthTextFieldWrapper>
        </div>
      </div>
      <FramerTooltip
        content={`${emailMessage} ${email}`}
        placement={PopoverPlacement.bottom}
      >
        <EmailIcon
          className={clsx("emailIcon", {
            emailIconActive: email,
            emailIconDisabled: !email,
          })}
          fontSize={"medium"}
          onClick={emailClickHandler}
        />
      </FramerTooltip>
      <FieldExplanationIcon formAttributes={formAttributes} />
    </StyledEmailTemplate>
  );
};
